import { type ReactNode, useEffect, useRef, useState } from 'react'
import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui/icon'

// Экосистемный свитчер в шапке: чип текущего продукта — единственный триггер
// панели со списком сервисов STIT. Текущий продукт помечен и ведёт на
// главную (`/`), остальные — прямые ссылки на прод-домены. Компонент копируется
// 1:1 между продуктами — отличаются только current и вордмарк, приходящие
// пропсами. Заказчик видит тот же свитчер в своём срезе: список плиток —
// продукты, выданные его аккаунту, таглайны — с его точки зрения.

export interface ProductLink {
  id: 'hub' | 'deal' | 'task' | 'telemost' | 'calendar'
  name: string
  tagline: string
  url: string
  glyph: ReactNode
}

// Глифы — из фавиконов продуктов (единый стиль: красный знак на тёмной плитке).
const GLYPH_HUB = (
  <svg viewBox="0 0 64 64" className="size-full" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#0a0a0a" />
    <path d="M32 32L32 14M32 32L16.5 41M32 32l15.5 9" stroke="#FE3232" strokeWidth="4" />
    <circle cx="32" cy="14" r="6" fill="#FE3232" />
    <circle cx="16.5" cy="41" r="6" fill="#FE3232" />
    <circle cx="47.5" cy="41" r="6" fill="#FE3232" />
    <circle cx="32" cy="32" r="7.5" fill="#FE3232" stroke="#0a0a0a" strokeWidth="3" />
  </svg>
)
const GLYPH_DEAL = (
  <svg viewBox="0 0 64 64" className="size-full" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#0a0a0a" />
    <path d="M19 12h17l11 11v29a4 4 0 0 1-4 4H19a4 4 0 0 1-4-4V16a4 4 0 0 1 4-4z" fill="#FE3232" />
    <path d="M36 12l11 11H36z" fill="#0a0a0a" opacity="0.35" />
    <path d="M22 34h20M22 41h13" stroke="#0a0a0a" strokeWidth="4" strokeLinecap="round" />
  </svg>
)
const GLYPH_TASK = (
  <svg viewBox="0 0 64 64" className="size-full" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#0a0a0a" />
    <rect x="14" y="14" width="36" height="36" rx="8" fill="#FE3232" />
    <path
      d="M23.5 33.5l6.5 6.5 11.5-13"
      stroke="#0a0a0a"
      strokeWidth="5.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)
const GLYPH_TELEMOST = (
  <svg viewBox="0 0 64 64" className="size-full" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#0a0a0a" />
    <rect x="11" y="20" width="28" height="24" rx="5" fill="#FE3232" />
    <path d="M43 27.5 53 21v22l-10-6.5z" fill="#FE3232" />
  </svg>
)
// Знак календаря — через currentColor: плитка наследует акцент темы
// (text-accent), а не хардкодит цвет, как остальные глифы-фавиконы.
const GLYPH_CALENDAR = (
  <svg viewBox="0 0 20 20" className="size-full text-accent" aria-hidden="true">
    <rect width="20" height="20" rx="4.5" fill="#0a0a0a" />
    <rect
      x="4.5"
      y="5.75"
      width="11"
      height="9.75"
      rx="1.75"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.25"
    />
    <path d="M4.5 8.75h11" stroke="currentColor" strokeWidth="1.25" />
    <path
      d="M7.5 3.75v2.25M12.5 3.75v2.25"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
    />
    <circle cx="7.5" cy="11.9" r="0.95" fill="currentColor" />
    <circle cx="10" cy="11.9" r="0.95" fill="currentColor" />
    <circle cx="12.5" cy="11.9" r="0.95" fill="currentColor" />
  </svg>
)

export const STIT_PRODUCTS: ProductLink[] = [
  {
    id: 'hub',
    name: 'STIT Hub',
    tagline: 'Компании, проекты, сотрудники',
    url: 'https://hub.stit.tech',
    glyph: GLYPH_HUB,
  },
  {
    id: 'deal',
    name: 'STIT Deal',
    tagline: 'CRM: сделки и документы',
    url: 'https://deal.stit.tech',
    glyph: GLYPH_DEAL,
  },
  {
    id: 'task',
    name: 'STIT Task',
    tagline: 'Трекер задач студии',
    url: 'https://task.stit.tech',
    glyph: GLYPH_TASK,
  },
  {
    id: 'telemost',
    name: 'STIT Телемост',
    tagline: 'Видеовстречи команды',
    url: 'https://telemost.stit.tech',
    glyph: GLYPH_TELEMOST,
  },
  {
    id: 'calendar',
    name: 'STIT Календарь',
    tagline: 'Календарь команды',
    url: 'https://calendar.stit.tech',
    glyph: GLYPH_CALENDAR,
  },
]

// Тот же продукт, названный со стороны клиента: заказчик приходит за своими
// созвонами и документами, а не за «CRM студии».
const CUSTOMER_TAGLINES: Record<ProductLink['id'], string> = {
  hub: 'Моя компания',
  deal: 'Проект и документы',
  task: 'Задачи по проекту',
  telemost: 'Мои созвоны',
  calendar: 'Календарь встреч',
}

interface ProductSwitcherProps {
  current: ProductLink['id']
  wordmark: string
  // Срез заказчика: продукты из claim `products` его сессии. Не задан —
  // сотрудник: вся экосистема и внутренние таглайны.
  customerProducts?: readonly string[]
}

export function ProductSwitcher({ current, wordmark, customerProducts }: ProductSwitcherProps) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
        return
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
      e.preventDefault()
      const items = Array.from(
        panelRef.current?.querySelectorAll<HTMLAnchorElement>('[role="menuitem"]') ?? [],
      )
      if (items.length === 0) return
      const idx = items.indexOf(document.activeElement as HTMLAnchorElement)
      const next =
        e.key === 'ArrowDown'
          ? items[(idx + 1) % items.length]
          : items[(idx - 1 + items.length) % items.length]
      next?.focus()
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    panelRef.current?.querySelector<HTMLAnchorElement>('[role="menuitem"]')?.focus()
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const currentGlyph = STIT_PRODUCTS.find((p) => p.id === current)?.glyph
  // Продукт, которого нет в `products` заказчика, не показываем вовсе:
  // заблокированного не предлагаем.
  const items = customerProducts
    ? STIT_PRODUCTS.filter((p) => customerProducts.includes(p.id)).map((p) => ({
        ...p,
        tagline: CUSTOMER_TAGLINES[p.id],
      }))
    : STIT_PRODUCTS

  return (
    <div ref={rootRef} className="relative flex min-w-0 items-center">
      {/* Чип текущего продукта — и указатель «где я», и жест «сменить сервис»:
          знак продукта + обводка держат кнопку заметной в шапке. На узком
          экране вордмарк уходит, чип сжимается до знака с chevron. */}
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Сервисы STIT — сейчас ${wordmark}`}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'group flex h-9 min-w-0 items-center gap-1.5 rounded-control border border-border-default bg-bg-elevated pr-1.5 pl-1.5 text-text-primary transition-colors hover:border-border-strong hover:bg-bg-raised focus-visible:ring-2 focus-visible:ring-border-accent focus-visible:outline-none sm:gap-2 sm:pr-2',
          open && 'border-border-strong bg-bg-raised',
        )}
      >
        <span className="size-6 shrink-0 overflow-hidden rounded-[7px] ring-1 ring-border-subtle">
          {currentGlyph}
        </span>
        <span className="hidden min-w-0 truncate font-mono text-sm leading-none font-medium tracking-wide sm:block">
          {wordmark}
        </span>
        <Icon
          name="chevron-down"
          size={14}
          className={cn(
            'shrink-0 text-text-tertiary transition-transform group-hover:text-text-secondary',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          aria-label="Сервисы STIT"
          // Мобайл — панель на всю ширину под шапкой; от sm — выпадашка под триггером.
          className="fixed inset-x-2 top-[calc(3.5rem+env(safe-area-inset-top))] z-50 grid grid-cols-2 gap-1.5 rounded-card border border-border-default bg-bg-elevated p-2 shadow-lg sm:absolute sm:inset-x-auto sm:top-full sm:left-0 sm:mt-2 sm:w-96"
        >
          <p className="col-span-2 px-1 pt-1 font-mono text-[10px] tracking-[0.08em] text-text-tertiary uppercase">
            Экосистема STIT
          </p>
          {items.map((p, i) => {
            const isCurrent = p.id === current
            // Нечётный хвост списка не оставляет дыру в сетке.
            const lastOdd = i === items.length - 1 && items.length % 2 === 1
            return (
              <a
                key={p.id}
                role="menuitem"
                href={isCurrent ? '/' : p.url}
                onClick={() => setOpen(false)}
                className={cn(
                  // Хайрлайн у всех плиток (дизайн-язык STIT), текущая — подложкой
                  // и пилюлей: цвет границы у токен-слоя глобальный, на нём не играем.
                  'flex flex-col gap-2 rounded-control border border-border-default p-2.5 transition-colors focus-visible:ring-2 focus-visible:ring-border-accent focus-visible:outline-none',
                  isCurrent ? 'bg-bg-raised' : 'hover:bg-bg-raised',
                  lastOdd && 'col-span-2',
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="size-8 shrink-0 overflow-hidden rounded-[9px] ring-1 ring-border-subtle">
                    {p.glyph}
                  </span>
                  {isCurrent && (
                    <span className="ml-auto shrink-0 rounded-pill border border-border-default px-1.5 py-0.5 font-mono text-[10px] tracking-wide text-text-tertiary uppercase">
                      текущий
                    </span>
                  )}
                </span>
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-sm text-text-primary">{p.name}</span>
                  <span className="text-xs leading-snug text-text-tertiary">{p.tagline}</span>
                </span>
              </a>
            )
          })}
        </div>
      )}
    </div>
  )
}
